import React, { useState, useEffect } from 'react';
import { confirmErpia, alertErpia } from './ConfirmDialog';
import { X, Download, Eye, Trash2 } from 'lucide-react';
import APIService from '../services/api';

const BASE = 'https://besoins-tat-production.up.railway.app';

// ===== GESTIONNAIRE QUESTIONS =====
// Une question s'ÉCOUTE, elle ne se lit pas : le candidat est au téléphone.
// L'écran montre donc ce qui sera VRAIMENT posé — la question, puis, en
// retrait, le second temps que l'agent ne pose qu'après la réponse au premier
// — et le niveau visé, parce que sept questions au même cran n'apprennent
// rien d'un candidat.
const NIVEAU_LIBELLE = { junior: 'Junior', confirme: 'Confirmé', senior: 'Senior', expert: 'Expert' };
const NIVEAUX_DEFAUT = ['junior', 'confirme', 'senior', 'expert'];

// La jauge : quatre crans, remplis jusqu'au niveau. Aucune couleur — une
// question difficile n'est ni un succès ni un danger.
const JaugeNiveau = ({ niveau, niveaux }) => {
  const liste = niveaux?.length ? niveaux : NIVEAUX_DEFAUT;
  const rang = liste.indexOf(niveau);
  if (rang < 0) return null;
  return (
    <span className="tq-jauge" title={`Niveau ${NIVEAU_LIBELLE[niveau] || niveau}`}>
      <span className="tq-jauge-crans" aria-hidden="true">
        {liste.map((n, i) => <span key={n} className={`tq-cran${i <= rang ? ' tq-cran--plein' : ''}`} />)}
      </span>
      <span className="tq-jauge-nom">{NIVEAU_LIBELLE[niveau] || niveau}</span>
    </span>
  );
};

export const TestQuestionsManager = ({ besoin, showNotification }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editRelance, setEditRelance] = useState('');
  const [newQuestion, setNewQuestion] = useState('');
  const [addingNew, setAddingNew] = useState(false);
  const [jdPerimee, setJdPerimee] = useState(false); // questions tirées d'une version antérieure de la fiche de poste
  // Le catalogue des niveaux vient du SERVEUR (aucune liste codée ici) ;
  // `null` = étagé, un choix à part entière et non l'absence de choix.
  const [niveaux, setNiveaux] = useState(NIVEAUX_DEFAUT);
  const [niveauVise, setNiveauVise] = useState(null);
  // Questions SIMPLES par défaut (28.09) ; « en deux temps » est un choix. Le
  // serveur dit le mode des questions en place (`deux_temps`) : l'interrupteur
  // part de là, et la régénération automatique garde ce même choix.
  const [deuxTempsChoisi, setDeuxTempsChoisi] = useState(false);

  useEffect(() => { loadQuestions(); }, [besoin.id]);

  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${APIService.getToken()}` };

  const loadQuestions = async () => {
    try {
      setLoading(true);
      // ?etat=1 : la liste ET sa fraîcheur. Le serveur régénère les questions
      // auto quand la fiche de poste change, mais s'il n'y est pas encore
      // arrivé (ou n'a pas pu), l'écran doit le dire plutôt que présenter des
      // questions tirées d'une version précédente du poste.
      const res = await fetch(`${BASE}/tests/besoin/${besoin.id}?etat=1`, { headers });
      const data = await res.json();
      const liste = Array.isArray(data) ? data : (data.questions || []);
      setQuestions(liste);
      setJdPerimee(data?.jd_perimee === true);
      if (Array.isArray(data?.niveaux) && data.niveaux.length) setNiveaux(data.niveaux);
      if (typeof data?.deux_temps === 'boolean') setDeuxTempsChoisi(data.deux_temps);
    } catch (e) { setQuestions([]); } finally { setLoading(false); }
  };

  const regenerate = async () => {
    const vise = niveauVise ? ` au niveau ${NIVEAU_LIBELLE[niveauVise] || niveauVise}` : ', étagées du plus accessible au plus exigeant';
    const forme = deuxTempsChoisi ? ', en deux temps' : ', en questions simples';
    if (!await confirmErpia({ message: `Régénérer les questions${vise}${forme}, à partir de la fiche de poste actuelle ? Les questions générées seront remplacées ; celles que vous avez écrites ou modifiées sont conservées.` })) return;
    setGenerating(true);
    try {
      const res = await fetch(`${BASE}/tests/generate`, {
        method: 'POST', headers,
        body: JSON.stringify({ besoinId: besoin.id, titre: besoin.titre, client: besoin.client, description: besoin.description, niveau: niveauVise || undefined, deux_temps: deuxTempsChoisi })
      });
      const data = await res.json();
      if (data.success) { setJdPerimee(false); showNotification('Questions régénérées', 'success'); await loadQuestions(); }
      else { showNotification(data.error || 'Erreur génération', 'error'); }
    } catch (e) { showNotification('Erreur génération', 'error'); } finally { setGenerating(false); }
  };

  const ouvrirEdition = (q) => { setEditingId(q.id); setEditText(q.question); setEditRelance(q.relance || ''); };

  const saveEdit = async (id) => {
    try {
      // Le second temps voyage avec le texte : le vider ici, c'est dire « cette
      // question tient en une fois », et le serveur l'entend comme un retrait.
      await fetch(`${BASE}/tests/question/${id}`, { method: 'PUT', headers,
        body: JSON.stringify({ question: editText, relance: editRelance }) });
      setQuestions(prev => prev.map(q => q.id === id
        ? { ...q, question: editText, relance: editRelance.trim() || null, source: 'manuel' } : q));
      setEditingId(null);
    } catch (e) { showNotification('Erreur modification', 'error'); }
  };

  const deleteQuestion = async (id) => {
    if (!await confirmErpia({ message: 'Supprimer cette question ?' })) return;
    try {
      const res = await fetch(`${BASE}/tests/question/${id}`, { method: 'DELETE', headers });
      const data = await res.json();
      if (data.success) {
        setQuestions(prev => prev.filter(q => Number(q.id) !== Number(id)));
        showNotification('Question supprimée', 'success');
      } else {
        showNotification('Erreur suppression: ' + (data.error || ''), 'error');
      }
    } catch (e) { showNotification('Erreur suppression', 'error'); }
  };

  const addQuestion = async () => {
    if (!newQuestion.trim()) return;
    try {
      const res = await fetch(`${BASE}/tests/question`, { method: 'POST', headers, body: JSON.stringify({ besoinId: besoin.id, question: newQuestion.trim() }) });
      const data = await res.json();
      if (data.success) { setQuestions(prev => [...prev, data.question]); setNewQuestion(''); setAddingNew(false); }
    } catch (e) { showNotification('Erreur ajout', 'error'); }
  };

  const deuxTemps = questions.filter(q => q.relance).length;

  return (
    <div className="erp-card tq-carte">
      <div className="tq-tete">
        <div>
          <p className="tq-kicker">Entretien technique</p>
          <h2 className="tq-titre">Questions posées au candidat</h2>
        </div>
        <div className="tq-actions">
          <span className="tq-compte">{questions.length} question{questions.length > 1 ? 's' : ''}{deuxTemps ? ` · ${deuxTemps} en deux temps` : ''}</span>
          <button onClick={() => setAddingNew(true)} className="erp-btn erp-btn--secondary erp-btn--sm">
            <i className="bi bi-plus-lg" /> Ajouter
          </button>
        </div>
      </div>

      {/* Le niveau visé, à côté du bouton qui s'en sert : le choix et son
          effet ne se séparent pas. « Étagé » est le réglage par défaut — c'est
          l'écart entre ce qu'un candidat tient et ce qu'il ne tient pas qui
          informe, pas sept questions au même cran. */}
      <div className="tq-actions" style={{ marginBottom: '13px' }}>
        <span className="tq-jauge-nom">Niveau visé</span>
        <div className="tq-niveaux" role="group" aria-label="Niveau visé pour la génération">
          <button type="button" onClick={() => setNiveauVise(null)} disabled={generating}
            className={`tq-niveau-bt${niveauVise === null ? ' tq-niveau-bt--on' : ''}`}
            aria-pressed={niveauVise === null}>Étagé</button>
          {niveaux.map(n => (
            <button key={n} type="button" onClick={() => setNiveauVise(n)} disabled={generating}
              className={`tq-niveau-bt${niveauVise === n ? ' tq-niveau-bt--on' : ''}`}
              aria-pressed={niveauVise === n}>{NIVEAU_LIBELLE[n] || n}</button>
          ))}
        </div>
        {/* Simples par défaut : une question, une réponse. « En deux temps »
            ajoute une relance posée APRÈS la réponse — plus long au téléphone,
            utile quand le poste demande de creuser. */}
        <span className="tq-jauge-nom">Questions</span>
        <div className="tq-niveaux" role="group" aria-label="Forme des questions générées">
          <button type="button" onClick={() => setDeuxTempsChoisi(false)} disabled={generating}
            className={`tq-niveau-bt${!deuxTempsChoisi ? ' tq-niveau-bt--on' : ''}`}
            aria-pressed={!deuxTempsChoisi}>Simples</button>
          <button type="button" onClick={() => setDeuxTempsChoisi(true)} disabled={generating}
            className={`tq-niveau-bt${deuxTempsChoisi ? ' tq-niveau-bt--on' : ''}`}
            aria-pressed={deuxTempsChoisi}
            title="Chaque question peut porter une relance, posée après la réponse du candidat">En deux temps</button>
        </div>
        <button onClick={regenerate} disabled={generating} className="erp-btn erp-btn--primary erp-btn--sm">
          <i className={generating ? 'bi bi-arrow-repeat erp-spin' : 'bi bi-arrow-repeat'} />
          {generating ? 'Génération…' : 'Régénérer'}
        </button>
      </div>

      {/* La fiche de poste a bougé depuis la génération : le serveur régénère
          tout seul, mais tant que ce n'est pas fait l'écran le DIT — passer un
          entretien sur les questions d'une version précédente du poste ne se
          voit pas autrement. */}
      {jdPerimee && !loading && (
        <div className="tq-alerte">
          <i className="bi bi-exclamation-triangle" />
          <span style={{ flex: 1 }}>
            La fiche de poste a été modifiée depuis la génération de ces questions. Elles sont mises à jour automatiquement dans quelques instants — ou tout de suite.
          </span>
          <button onClick={regenerate} disabled={generating} className="tq-alerte-bt">
            {generating ? 'Génération…' : 'Régénérer'}
          </button>
        </div>
      )}

      {loading ? <div className="tq-chargement">Chargement…</div>
      : questions.length === 0 ? (
        <div className="tq-vide">
          <i className="bi bi-patch-question" />
          <p>Aucune question pour ce poste. Elles sont tirées de la fiche de poste.</p>
          <button onClick={regenerate} disabled={generating} className="erp-btn erp-btn--primary erp-btn--sm">
            {generating ? 'Génération…' : 'Générer les questions'}
          </button>
        </div>
      ) : (
        <div className="tq-liste">
          {questions.map((q, i) => (
            <div key={q.id} className="tq-item">
              <span className="tq-num">Q{i + 1}</span>
              {editingId === q.id ? (
                <div className="tq-edit">
                  <div className="tq-edit-ligne">
                    <input value={editText} onChange={e => setEditText(e.target.value)} className="erp-field"
                      onKeyDown={e => { if (e.key === 'Enter') saveEdit(q.id); if (e.key === 'Escape') setEditingId(null); }}
                      autoFocus />
                  </div>
                  <div className="tq-edit-ligne">
                    <span className="tq-edit-label">2ᵉ temps</span>
                    <input value={editRelance} onChange={e => setEditRelance(e.target.value)} className="erp-field"
                      placeholder="Posé seulement après sa réponse — laisser vide si la question tient en une fois"
                      onKeyDown={e => { if (e.key === 'Enter') saveEdit(q.id); if (e.key === 'Escape') setEditingId(null); }} />
                  </div>
                  <div className="tq-edit-ligne" style={{ justifyContent: 'flex-end' }}>
                    <button onClick={() => setEditingId(null)} className="erp-btn erp-btn--secondary erp-btn--sm">Annuler</button>
                    <button onClick={() => saveEdit(q.id)} className="erp-btn erp-btn--primary erp-btn--sm">Enregistrer</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="tq-corps">
                    <p className="tq-texte">{q.question}</p>
                    {q.relance && (
                      <p className="tq-relance">
                        <span className="tq-relance-label">2ᵉ temps — posé après sa réponse</span>
                        {q.relance}
                      </p>
                    )}
                    {(q.niveau || q.source === 'manuel') && (
                      <span className="tq-meta">
                        <JaugeNiveau niveau={q.niveau} niveaux={niveaux} />
                        {q.source === 'manuel' && <span className="tq-source">Écrite à la main</span>}
                      </span>
                    )}
                  </div>
                  <div className="tq-outils">
                    <button onClick={() => ouvrirEdition(q)} className="tq-outil" title="Modifier cette question">
                      <i className="bi bi-pencil" />
                    </button>
                    <button onClick={() => deleteQuestion(q.id)} className="tq-outil tq-outil--danger" title="Supprimer cette question">
                      <i className="bi bi-trash" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {addingNew && (
        <div className="tq-ajout">
          <input value={newQuestion} onChange={e => setNewQuestion(e.target.value)}
            placeholder="Nouvelle question technique…"
            className="erp-field"
            onKeyDown={e => { if (e.key === 'Enter') addQuestion(); if (e.key === 'Escape') setAddingNew(false); }}
            autoFocus />
          <button onClick={addQuestion} className="erp-btn erp-btn--primary erp-btn--sm">Ajouter</button>
          <button onClick={() => setAddingNew(false)} className="erp-btn erp-btn--secondary erp-btn--sm">Annuler</button>
        </div>
      )}
    </div>
  );
};

// ===== SESSION DE TEST =====
export const TestSession = ({ candidat, besoin, questions, onClose, onComplete, showNotification }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [reponses, setReponses] = useState(questions.map(q => ({ question: q.question, reponse: '' })));
  const [evaluating, setEvaluating] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = s => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;
  const setReponse = text => setReponses(prev => prev.map((r, i) => i === currentIndex ? { ...r, reponse: text } : r));
  const answered = reponses.filter(r => r.reponse.trim()).length;
  const progress = ((currentIndex + 1) / questions.length) * 100;

  const handleEvaluate = async () => {
    console.log('Démarrage évaluation...', candidat.id, besoin.id);
    setEvaluating(true);
    try {
      const res = await fetch(`${BASE}/tests/evaluate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${APIService.getToken()}` },
        body: JSON.stringify({ candidatId: candidat.id, besoinId: besoin.id, candidatNom: candidat.nom, titreBesoin: besoin.titre, reponses })
      });
      console.log('📥 HTTP status:', res.status);
      const data = await res.json();
      console.log('📥 Response:', JSON.stringify(data).substring(0, 200));
      if (data.success) { onComplete(data); }
      else { showNotification('Erreur: ' + (data.error || 'Erreur évaluation'), 'error'); }
    } catch (e) {
      console.error('Exception:', e);
      showNotification('Erreur évaluation: ' + e.message, 'error');
    } finally { setEvaluating(false); }
  };

  return (
    <div className="fixed inset-0 bg-gray-950 flex flex-col z-[70]">
      <div className="bg-gray-900 border-b border-gray-800 px-6 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <div className="text-white font-bold text-sm">Test technique</div>
          <div className="text-gray-400 text-sm">{candidat.nom} — {besoin.titre}</div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-center">
            <div className="text-xs text-gray-500">Répondues</div>
            <div className="text-white font-bold text-sm">{answered}/{questions.length}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-gray-500">Durée</div>
            <div className="text-white font-bold text-sm font-mono">{formatTime(elapsed)}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-sm">✕ Annuler</button>
        </div>
      </div>

      <div className="h-1 bg-gray-800 flex-shrink-0">
        <div className="h-full bg-indigo-500 transition-all duration-300" style={{ width: `${progress}%` }} />
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="w-64 bg-gray-900 border-r border-gray-800 overflow-y-auto p-3 flex-shrink-0">
          {questions.map((q, i) => (
            <button key={q.id} onClick={() => setCurrentIndex(i)}
              className={`w-full text-left p-2.5 rounded-lg mb-1.5 text-xs ${
                i === currentIndex ? 'bg-indigo-600 text-white' :
                reponses[i].reponse.trim() ? 'bg-gray-800 text-green-400' : 'bg-gray-800 text-gray-400'
              }`}>
              <div className="flex items-center gap-2 mb-1">
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                  i === currentIndex ? 'bg-white text-indigo-600' :
                  reponses[i].reponse.trim() ? 'bg-green-500 text-white' : 'bg-gray-700 text-gray-400'
                }`}>{reponses[i].reponse.trim() ? '✓' : i+1}</span>
                <span className="truncate">Q{i+1}</span>
              </div>
              <p className="text-xs opacity-70 truncate">{q.question.substring(0, 50)}...</p>
            </button>
          ))}
        </div>

        <div className="flex-1 flex flex-col p-8 overflow-auto">
          <div className="max-w-2xl mx-auto w-full">
            <div className="text-indigo-400 text-xs font-medium mb-3">QUESTION {currentIndex + 1} / {questions.length}</div>
            <div className="bg-gray-800 rounded-xl p-6 mb-6">
              <p className="text-white text-lg leading-relaxed">{questions[currentIndex].question}</p>
              {/* Le second temps est posé APRÈS la réponse au premier — ici
                  aussi : le recruteur qui mène l'entretien à la main suit le
                  même déroulé que l'agent, sinon les deux ne posent pas la
                  même chose. Et il ne relance pas quelqu'un qui vient de dire
                  qu'il ne sait pas. */}
              {questions[currentIndex].relance && (
                <p className="tq-relance" style={{ marginTop: '14px', color: 'var(--erp-text-tertiary)' }}>
                  <span className="tq-relance-label">2ᵉ temps — seulement après sa réponse</span>
                  {questions[currentIndex].relance}
                </p>
              )}
            </div>
            <div className="mb-6">
              <label className="text-gray-400 text-xs font-medium block mb-2">RÉPONSE DU CANDIDAT</label>
              <textarea value={reponses[currentIndex].reponse} onChange={e => setReponse(e.target.value)}
                placeholder="Saisissez ici la réponse du candidat..."
                rows={8}
                className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:ring-2 focus:ring-indigo-500 outline-none resize-none text-sm"
                autoFocus />
            </div>
            <div className="flex justify-between items-center">
              <button onClick={() => setCurrentIndex(i => Math.max(0, i - 1))} disabled={currentIndex === 0}
                className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 disabled:opacity-30 text-sm">
                ← Précédente
              </button>
              {currentIndex < questions.length - 1 ? (
                <button onClick={() => setCurrentIndex(i => i + 1)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">
                  Suivante →
                </button>
              ) : (
                <button onClick={handleEvaluate} disabled={evaluating}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium flex items-center gap-2">
                  {evaluating ? <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />Évaluation...</> : 'Générer le rapport'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ===== RAPPORT DE TEST =====
// UN SEUL RAPPORT, DEUX PORTES. Le rapport qui s'ouvre juste après un
// entretien et celui qu'on relit depuis l'historique sont le MÊME document :
// deux copies divergeraient au premier ajustement (elles l'avaient déjà fait
// — le bandeau « non évaluable » n'existait que d'un côté). Le corps et le
// PDF vivent donc ici, une fois.
const tonScore = sc => sc >= 70 ? 'haut' : sc >= 50 ? 'moyen' : 'bas';
const tonNote = n => (n ?? 0) >= 4 ? 'haut' : (n ?? 0) >= 2 ? 'moyen' : 'bas';

// SUR QUOI REPOSE CE RAPPORT. Un score de 40 % ne se lit pas pareil selon
// qu'il vient de sept réponses ou de deux. Quand le serveur a RETENU le
// verdict (trop peu de réponses exploitables — ligne coupée, voix mal
// comprise), on n'affiche ni note ni recommandation : le rapport garde ce qui
// a été dit, et dit qu'il faut replanifier. Noter quelqu'un sur un entretien
// à moitié entendu, c'est le condamner pour un incident.
const lireRapport = (source) => {
  const rapport = source?.rapport?.rapport || source?.rapport || {};
  const reponses = source?.rapport?.reponses || source?.reponses || [];
  const couverture = source?.rapport?.couverture || source?.couverture || null;
  return { rapport, reponses, couverture, verdictRetenu: !!couverture && couverture.suffisant === false,
    sc: Number(rapport?.score_global || 0) };
};

// RÉÉCOUTER L'ENTRETIEN, depuis le rapport — donc depuis le besoin ET depuis
// le calendrier, puisque les deux ouvrent ce même corps. Le serveur sert
// `enregistrement` (disponible | en_attente | sans_objet) et, quand il y a
// quelque chose à écouter, `enregistrement_url` mot de passe compris.
//
// « en attente » se DIT : l'enregistrement arrive quelques minutes après la
// fin de l'appel. Ne rien afficher ferait croire qu'il n'y en a pas.
//
// `rapproche === 'deduit'` : le rapport est antérieur au rattachement direct,
// l'appel a été retrouvé par déduction. On le signale plutôt que de laisser
// écouter un autre entretien du même candidat en croyant que c'est celui-là.
const EcouteRapport = ({ source }) => {
  const etat = source?.enregistrement;
  if (etat === 'disponible' && source.enregistrement_url) {
    return (
      <div className="rap-ecoute">
        <a href={source.enregistrement_url} target="_blank" rel="noreferrer" className="erp-btn erp-btn--secondary erp-btn--sm">
          <i className="bi bi-play-circle" /> Écouter l'enregistrement
        </a>
        {source.rapproche === 'deduit' && (
          <span className="rap-ecoute-note">Enregistrement rattaché à ce rapport d'après sa date — vérifiez qu'il s'agit du bon entretien.</span>
        )}
      </div>
    );
  }
  if (etat === 'en_attente') {
    return <div className="rap-ecoute"><span className="rap-ecoute-note">{source.enregistrement_message}</span></div>;
  }
  return null;
};

const RapportCorps = ({ source, date }) => {
  const { rapport, reponses, couverture, verdictRetenu, sc } = lireRapport(source);
  return (
    <>
      <EcouteRapport source={source} />
      {verdictRetenu ? (
        <div className="rap-retenu">
          <div className="rap-retenu-titre">Entretien non évaluable — pas de note</div>
          <p>
            {couverture.phrase || `${couverture.repondues} réponse(s) sur ${couverture.posees}`} : trop peu pour juger.
            Ce qui a été dit est conservé ci-dessous, mais aucune note ni recommandation n'est portée au dossier —
            un entretien à moitié entendu se relance, il ne se note pas.
          </p>
        </div>
      ) : (
        <div className="rap-score">
          <div className={`rap-score-bloc rap-score--${tonScore(sc)}`}>
            <div className="rap-score-chiffre">{sc}%</div>
            <div className="rap-score-label">Score global</div>
          </div>
          <div className="rap-score-dit">
            {rapport?.recommandation && <span className="erp-chip rap-verdict">{rapport.recommandation}</span>}
            {rapport?.synthese && <p className="rap-synthese">{rapport.synthese}</p>}
            {/* Même quand la note tient, on dit sur combien de réponses. */}
            {couverture && couverture.posees > 0 && couverture.repondues < couverture.posees && (
              <p className="rap-appui">Note établie sur {couverture.phrase}.</p>
            )}
            {date && <p className="rap-date">{new Date(date).toLocaleDateString('fr-FR')}</p>}
          </div>
        </div>
      )}

      <div className="rap-cols">
        <div className="rap-bloc rap-bloc--forts">
          <h3 className="rap-bloc-titre">Points forts</h3>
          {rapport?.points_forts?.length
            ? <ul>{rapport.points_forts.map((p, i) => <li key={i}>{p}</li>)}</ul>
            : <p className="rap-bloc-vide">Rien de relevé.</p>}
        </div>
        <div className="rap-bloc rap-bloc--lacunes">
          <h3 className="rap-bloc-titre">Lacunes</h3>
          {rapport?.lacunes?.length
            ? <ul>{rapport.lacunes.map((l, i) => <li key={i}>{l}</li>)}</ul>
            : <p className="rap-bloc-vide">Rien de relevé.</p>}
        </div>
      </div>

      <div>
        <h3 className="rap-section-titre">Détail des réponses</h3>
        <div className="rap-detail">
          {reponses.map((r, i) => {
            const ev = rapport?.evaluations?.[i] || {};
            return (
              <div key={i} className="rap-q">
                <div className="rap-q-tete">
                  <span className="rap-q-num">Q{i + 1}</span>
                  <p className="rap-q-texte">{r.question}</p>
                  {/* Pas de note quand le verdict est retenu : un « 0/5 »
                      posé sur une réponse qu'on n'a pas entendue se lit comme
                      un jugement, alors que c'est un incident de ligne. */}
                  <span className={`rap-note${verdictRetenu ? '' : ` rap-note--${tonNote(ev.note)}`}`}>
                    {verdictRetenu ? '—' : `${ev.note ?? '-'}/5`}
                  </span>
                </div>
                <p className={`rap-rep${r.reponse ? '' : ' rap-rep--vide'}`}>{r.reponse || 'Pas de réponse'}</p>
                {ev.commentaire && !verdictRetenu && <p className="rap-comm">{ev.commentaire}</p>}
              </div>
            );
          })}
          {!reponses.length && <p className="rap-vide">Aucune réponse enregistrée pour cet entretien.</p>}
        </div>
      </div>
    </>
  );
};

// Le PDF : mêmes données, une seule fabrique. Ce qui change d'une porte à
// l'autre est passé en paramètres — le nom en tête, la ligne de contexte, la
// date et le nom de fichier.
const genererPdfRapport = async ({ source, nom, contexte, date }) => {
  const { rapport, reponses, verdictRetenu, sc } = lireRapport(source);
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = 210, pageH = 297, mL = 18, mR = 18;
  const navy = [28,46,90], teal = [0,184,162], white = [255,255,255];
  const dark = [35,31,32], gray = [120,120,120];
  const sf = (c) => doc.setFillColor(c[0],c[1],c[2]);
  const st = (c) => doc.setTextColor(c[0],c[1],c[2]);
  const quand = new Date(date || Date.now());
  const sr = verdictRetenu ? gray : sc>=70?[34,197,94]:sc>=50?[249,115,22]:[239,68,68];

  sf(navy); doc.rect(0,0,pageW,28,'F');
  sf(teal); doc.rect(0,28,pageW,1.2,'F');
  doc.setFont('helvetica','bold'); doc.setFontSize(14); st(white);
  doc.text('RAPPORT DE TEST TECHNIQUE', mL, 13);
  doc.setFont('helvetica','normal'); doc.setFontSize(8); st(teal);
  doc.text(APIService.getOrgName(), mL, 20);
  st(white); doc.text(quand.toLocaleDateString('fr-FR'), pageW-mR, 13, {align:'right'});

  let y = 38;
  sf([245,250,252]); doc.roundedRect(mL,y,pageW-mL-mR,20,2,2,'F');
  doc.setFont('helvetica','bold'); doc.setFontSize(12); st(navy);
  doc.text(String(nom || ''), mL+4, y+8);
  doc.setFont('helvetica','normal'); doc.setFontSize(9); st(gray);
  doc.text(String(contexte || ''), mL+4, y+15);
  y += 26;

  // Le PDF dit la même chose que l'écran : pas de note quand le verdict est
  // retenu. Un PDF part par email et survit à la conversation — c'est la
  // dernière place où l'on peut laisser croire à un « 0 % ».
  sf(sr); doc.roundedRect(mL,y,40,18,2,2,'F');
  doc.setFont('helvetica','bold'); doc.setFontSize(verdictRetenu?11:20); st(white);
  doc.text(verdictRetenu ? 'N/A' : String(sc)+'%', mL+20, y+12, {align:'center'});
  doc.setFontSize(9); st(navy); doc.setFont('helvetica','bold');
  doc.text(verdictRetenu ? 'Entretien non evaluable' : 'Score global', mL+45, y+6);
  doc.setFont('helvetica','normal'); st(sr);
  doc.text(verdictRetenu ? 'Trop peu de reponses exploitables — a replanifier'
    : String(rapport?.recommandation||''), mL+45, y+13);
  st(dark); doc.setFontSize(8);
  const synthLines = doc.splitTextToSize(String(rapport?.synthese||''), pageW-mL-mR-45);
  synthLines.slice(0,2).forEach((l,i) => doc.text(l, mL+45, y+18+i*5));
  y += 28;

  sf(teal); doc.rect(mL,y,pageW-mL-mR,0.5,'F');
  doc.setFont('helvetica','bold'); doc.setFontSize(10); st(teal);
  doc.text('DÉTAIL DES RÉPONSES', mL, y+6); y += 12;

  (reponses||[]).forEach((r,i) => {
    const ev = rapport?.evaluations?.[i] || {};
    if (y+30 > pageH-20) { doc.addPage(); y=20; }
    doc.setFont('helvetica','bold'); doc.setFontSize(9); st(navy);
    doc.text(`Q${i+1}. ${(r.question||'').substring(0,80)}`, mL, y); y+=5;
    doc.setFont('helvetica','normal'); doc.setFontSize(8); st(dark);
    doc.splitTextToSize(r.reponse||'(Pas de réponse)', pageW-mL-mR-15).slice(0,3).forEach(l => { doc.text(l,mL+4,y); y+=4.5; });
    if (!verdictRetenu) {
      const nc = (ev.note||0)>=4?[34,197,94]:(ev.note||0)>=2?[249,115,22]:[239,68,68];
      doc.setFont('helvetica','italic'); doc.setFontSize(8); st(nc);
      doc.text(`Note: ${ev.note||'-'}/5 — ${ev.commentaire||''}`, mL+4, y); y+=7;
    } else { y+=3; }
  });

  if (y+30>pageH-20) { doc.addPage(); y=20; }
  sf(teal); doc.rect(mL,y,pageW-mL-mR,0.5,'F');
  doc.setFont('helvetica','bold'); doc.setFontSize(10); st(teal);
  doc.text('SYNTHÈSE', mL, y+6); y+=12;
  doc.setFontSize(9); st([34,197,94]); doc.text('Points forts', mL, y); y+=5;
  (rapport?.points_forts||[]).forEach(p => { doc.setFont('helvetica','normal'); doc.setFontSize(8); st(dark); doc.text(`• ${p}`,mL+3,y); y+=5; });
  y+=3;
  doc.setFont('helvetica','bold'); doc.setFontSize(9); st([239,68,68]); doc.text('Lacunes', mL, y); y+=5;
  (rapport?.lacunes||[]).forEach(l => { doc.setFont('helvetica','normal'); doc.setFontSize(8); st(dark); doc.text(`• ${l}`,mL+3,y); y+=5; });

  const total = doc.internal.getNumberOfPages();
  for (let p=1;p<=total;p++) { doc.setPage(p); sf(navy); doc.rect(0,pageH-12,pageW,12,'F'); doc.setFont('helvetica','normal'); doc.setFontSize(7); st(white); doc.text(`${APIService.getOrgName()} — Rapport Test Technique`,mL,pageH-5); doc.text(`Page ${p}/${total}`,pageW-mR,pageH-5,{align:'right'}); }

  doc.save(`${APIService.getOrgName('Societe').replace(/[^A-Za-z0-9]+/g,'_')}_Test_${String(nom||'candidat').replace(/\s+/g,'_')}_${new Date().toISOString().slice(0,10)}.pdf`);
};

export const TestReport = ({ result, candidat, besoin, onClose }) => {
  const telecharger = () => genererPdfRapport({
    source: result, nom: candidat.nom,
    contexte: `Poste : ${besoin.titre} — Client : ${besoin.client}`, date: Date.now() });

  return (
    <div className="rap-fond" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="rap-panneau" role="dialog" aria-label="Rapport de test technique">
        <div className="rap-tete">
          <div>
            <p className="rap-kicker">Entretien technique</p>
            <h2 className="rap-titre">Rapport de {candidat.nom}</h2>
            <p className="rap-sous">{besoin.titre}{besoin.client ? ` — ${besoin.client}` : ''}</p>
          </div>
          <div className="rap-tete-actions">
            <button onClick={telecharger} className="erp-btn erp-btn--secondary erp-btn--sm">
              <Download className="w-4 h-4" /> PDF
            </button>
            <button onClick={onClose} className="erp-btn erp-btn--secondary erp-btn--sm" aria-label="Fermer">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="rap-corps">
          <RapportCorps source={result} />
        </div>
      </div>
    </div>
  );
};

// ===== HISTORIQUE DES TESTS =====
export const TestSessionsHistory = ({ candidatId, refreshKey }) => {
  const [sessions, setSessions] = useState([]);
  const [viewingSession, setViewingSession] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => { loadSessions(); }, [candidatId, refreshKey]);

  const loadSessions = async () => {
    try {
      const res = await fetch(`${BASE}/tests/sessions/${candidatId}`, {
        headers: { 'Authorization': `Bearer ${APIService.getToken()}` }
      });
      const data = await res.json();
      setSessions(Array.isArray(data) ? data : []);
    } catch (e) { setSessions([]); }
  };

  // Suppression d'un rapport : DELETE en miroir du GET /tests/session/:id.
  // Route pas encore côté serveur (404/405/501) → on le dit, rien ne bouge à
  // l'écran ; le rapport ne disparaît que sur confirmation du serveur.
  const supprimerSession = async (session) => {
    const quand = new Date(session.createdAt).toLocaleDateString('fr-FR');
    const ok = await confirmErpia({
      title: 'Supprimer ce rapport ?',
      message: `Le rapport de test du ${quand} (score ${session.score}%) sera définitivement supprimé.`,
      confirmLabel: 'Supprimer le rapport',
      tone: 'danger',
    });
    if (!ok) return;
    setDeletingId(session.id);
    try {
      const res = await fetch(`${BASE}/tests/session/${session.id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${APIService.getToken()}` }
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 404 || res.status === 405 || res.status === 501) {
        alertErpia({ message: data.error || data.message || 'La suppression des rapports arrive côté serveur — rien n’a été supprimé.' });
        return;
      }
      if (!res.ok || data.success === false) throw new Error(data.error || data.message || `HTTP ${res.status}`);
      setSessions(prev => prev.filter(s => s.id !== session.id));
    } catch (e) {
      alertErpia({ title: 'Suppression impossible', message: e.message, tone: 'danger' });
    } finally { setDeletingId(null); }
  };

  if (sessions.length === 0) return null;

  return (
    <div className="erp-card tq-carte" style={{ marginTop: '8px' }}>
      <div className="tq-tete" style={{ marginBottom: '10px' }}>
        <div>
          <p className="tq-kicker">Entretien technique</p>
          <h4 className="tq-titre">Rapports</h4>
        </div>
        <span className="tq-compte">{sessions.length} rapport{sessions.length > 1 ? 's' : ''}</span>
      </div>
      <div className="tq-liste">
        {sessions.map(session => (
          <div key={session.id} onClick={() => setViewingSession(session)} className="rap-ligne"
            role="button" tabIndex={0}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setViewingSession(session); } }}>
            {/* Le score porte son filet, comme dans le rapport : c'est le même
                fait, il se lit pareil aux deux endroits. */}
            <span className={`rap-ligne-score rap-score--${tonScore(Number(session.score) || 0)}`}>{session.score}%</span>
            <span className="erp-chip">{session.recommandation}</span>
            <span className="rap-ligne-date">
              {new Date(session.createdAt).toLocaleDateString('fr-FR')} · {new Date(session.createdAt).toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'})}
            </span>
            <span className="rap-ligne-ouvrir"><Eye className="w-3.5 h-3.5" /> Ouvrir</span>
            <button onClick={e => { e.stopPropagation(); supprimerSession(session); }}
              disabled={deletingId != null}
              className="tq-outil tq-outil--danger" title="Supprimer ce rapport de test">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      {viewingSession && (
        <TestSessionViewer sessionId={viewingSession.id} onClose={() => setViewingSession(null)} />
      )}
    </div>
  );
};

// ===== VIEWER SESSION =====
// La MÊME porte que TestReport, sur un rapport relu plus tard : même corps,
// même PDF. Ce qui change, c'est d'où vient le rapport (le serveur) et le fait
// qu'il faut l'attendre.
const TestSessionViewer = ({ sessionId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${BASE}/tests/session/${sessionId}`, {
          headers: { 'Authorization': `Bearer ${APIService.getToken()}` }
        });
        const d = await res.json().catch(() => ({}));
        // Le message du serveur fait foi : « rapport introuvable » et
        // « rapport pas encore calculé » ne se corrigent pas pareil.
        if (!res.ok) { setErreur(d.error || d.message || `Rapport indisponible (HTTP ${res.status})`); return; }
        setData(d);
      } catch (e) { setErreur('Rapport indisponible pour le moment.'); } finally { setLoading(false); }
    };
    load();
  }, [sessionId]);

  const nom = data?.candidatNom || (data ? `Candidat #${data.candidat_id}` : '');
  const telecharger = () => genererPdfRapport({
    source: data, nom,
    contexte: `Test technique — ${new Date(data?.createdAt || Date.now()).toLocaleDateString('fr-FR')}`,
    date: data?.createdAt });

  return (
    <div className="rap-fond rap-fond--haut" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="rap-panneau rap-panneau--etroit" role="dialog" aria-label="Rapport de test technique">
        <div className="rap-tete">
          <div>
            <p className="rap-kicker">Entretien technique</p>
            <h3 className="rap-titre">Rapport{nom ? ` de ${nom}` : ''}</h3>
            {data?.createdAt && <p className="rap-sous">{new Date(data.createdAt).toLocaleDateString('fr-FR')}</p>}
          </div>
          <div className="rap-tete-actions">
            {data && !loading && (
              <button onClick={telecharger} className="erp-btn erp-btn--secondary erp-btn--sm">
                <Download className="w-3.5 h-3.5" /> PDF
              </button>
            )}
            <button onClick={onClose} className="erp-btn erp-btn--secondary erp-btn--sm" aria-label="Fermer">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="rap-corps">
          {loading ? (
            <div className="rap-vide">
              <i className="bi bi-arrow-repeat tq-spin" style={{ fontSize: '18px' }} />
              <div style={{ marginTop: '8px' }}>Chargement…</div>
            </div>
          ) : erreur ? <p className="rap-vide">{erreur}</p>
          : !data ? <p className="rap-vide">Rapport non disponible</p>
          : <RapportCorps source={data} date={data.createdAt} />}
        </div>
      </div>
    </div>
  );
};
